import _get from 'lodash/get';
import { DYNAMIC_GREETING_MODES } from '../../../../stores/profile/use-participant-holdings-store';
import { DYNAMIC_GREETING_URLS } from './dynamic-greeting-urls';
import moment from 'moment-msdate';
import globals from '../../../../utils/globals';

const getModeForSingleFund = fund => {
  const extractedHolding = _get(fund, '[0]');
  const activeDate = _get(extractedHolding, '[0].activeDate');
  const inactiveDate = _get(extractedHolding, '[0].inactiveDate');
  const transfer = _get(extractedHolding, '[0].transfer');
  const contribution = _get(extractedHolding, '[0].contribution');
  const planConsent = _get(extractedHolding, '[0].planConsent');
  const institutionName = _get(extractedHolding, '[0].planLegalName');
  const investmentLongName = _get(extractedHolding, '[0].investmentLongName');
  const singleInvestmentObj = {
    institutionName,
    activeDate,
    inactiveDate,
    investmentLongName,
    fundMode: undefined
  };
  // Dynamic Greeting: Calculating investment changing scenarios
  if (moment(activeDate).isAfter() && planConsent) {
    singleInvestmentObj.fundMode = DYNAMIC_GREETING_MODES.opening;
  } else if (transfer !== 'Y' || contribution !== 'Y') {
    singleInvestmentObj.fundMode = DYNAMIC_GREETING_MODES.restricted;
  } else if (moment(inactiveDate).isAfter() && planConsent) {
    singleInvestmentObj.fundMode = DYNAMIC_GREETING_MODES.closing;
  }
  return singleInvestmentObj;
};


export const getInvestmentChangeModes = ({ holdingsIncludingCurrentFund }) => {
  const isMultipleFunds = holdingsIncludingCurrentFund.length > 1;
  const multipleChangeData = [];
  const investmentChangeObj = {
    investmentChangingMode: '',
    multipleChangeData: []
  };
  if (!isMultipleFunds) {
    const singleInvestmentData = getModeForSingleFund(holdingsIncludingCurrentFund);
    investmentChangeObj.investmentChangingMode = singleInvestmentData.fundMode;
    return { ...singleInvestmentData, ...investmentChangeObj };
  }
  holdingsIncludingCurrentFund.forEach(fund => {
    const investmentData = getModeForSingleFund([fund]);
    if (investmentData.fundMode) {
      multipleChangeData.push(investmentData);
    }
  });
  // Dynamic Greeting: If there are multiple funds but only one fund in them has changes treat it like a single fund which is changing.
  if (multipleChangeData.length === 1) {
    investmentChangeObj.investmentChangingMode = multipleChangeData[0].fundMode;
    return { ...multipleChangeData[0], ...investmentChangeObj };
  }
  if (multipleChangeData.length > 1) {
    investmentChangeObj.investmentChangingMode = DYNAMIC_GREETING_MODES.multipleChanges;
    investmentChangeObj.multipleChangeData = multipleChangeData;
  }
  return investmentChangeObj;
};

const computeModalMessage = ({ activeDate, inactiveDate, mode }) => {
  switch (mode) {
    case DYNAMIC_GREETING_MODES.closing:
      return `The investment will not be available to receive new contributions (or transferred accumulations) as of ${inactiveDate}, and existing accumulations will be "mapped out" to a new investment.`;
    case DYNAMIC_GREETING_MODES.opening:
      return `The investment will be available to receive new contributions (or transferred accumulations) as of ${activeDate}.`;
    case DYNAMIC_GREETING_MODES.restricted:
      return 'The investment will not be available to receive new contributions (or transferred accumulations), but existing accumulations in the investment may remain invested in it.';
    default:
      return undefined;
  }
};

export const getFormattedDynamicGreetingParameters = ({ dynamicGreetingMode, holdingsIncludingCurrentFund }) => {
  const { holdingsAvailable, holdingsHeld } = holdingsIncludingCurrentFund;
  const dynamicGreetingArr = [];
  const environment = globals.env;
  let urlPrefix;
  if (environment === 'production') {
    urlPrefix = 'shared.tiaa.org';
  } else {
    urlPrefix = 'origin-shared-st2.test.tiaa.org';
  }

  dynamicGreetingMode.forEach(mode => {
    const dynamicGreetingData = {
      greetingText: '',
      linkText: '',
      modalMessages: [],
      modalTitle: '',
      url: '',
      variant: 'DynamicGreetingCheck'
    };
    const isHeld = mode === DYNAMIC_GREETING_MODES.held;
    const holdings = isHeld ? holdingsHeld : holdingsAvailable;
    const isMultipleFunds = holdings.length > 1;
    const { activeDate, inactiveDate, institutionName, investmentChangingMode, investmentLongName, multipleChangeData } = getInvestmentChangeModes({ holdingsIncludingCurrentFund: holdings });
    const formattedActiveDate = activeDate ? moment(activeDate).format('MMMM D') : undefined;
    const formattedInactiveDate = inactiveDate ? moment(inactiveDate).format('MMMM D') : undefined;
    const messageData = {
      institutionName,
      message: '',
      title: investmentLongName
    };
    switch (mode) {
      case DYNAMIC_GREETING_MODES.available:
        dynamicGreetingData.greetingText = isMultipleFunds ? 'This investment is available in multiple retirement plans. ' : `This investment is available in your ${institutionName} retirement plan. `;
        dynamicGreetingData.linkText = 'Invest now.';
        dynamicGreetingData.url = `https://${urlPrefix}${DYNAMIC_GREETING_URLS.chooseTransaction}`;
        break;
      case DYNAMIC_GREETING_MODES.closing:
        dynamicGreetingData.greetingText = `This investment is closing in your ${institutionName} retirement plan on ${formattedInactiveDate}. `;
        messageData.message = computeModalMessage({ inactiveDate: formattedInactiveDate, mode: investmentChangingMode });
        dynamicGreetingData.linkText = 'Read more.';
        dynamicGreetingData.modalMessages.push(messageData);
        break;
      case DYNAMIC_GREETING_MODES.held:
        dynamicGreetingData.greetingText = isMultipleFunds ? 'You hold this investment in multiple retirement plans. ' : `You hold this investment in your ${institutionName} retirement plan. `;
        dynamicGreetingData.linkText = 'Exchange now.';
        dynamicGreetingData.url = `https://${urlPrefix}${DYNAMIC_GREETING_URLS.chooseTransaction}`;
        break;
      case DYNAMIC_GREETING_MODES.multipleChanges:
        // Dynamic Greeting: multipleChangeData holds change scenario for more than one fund that has changes.
        multipleChangeData.forEach(data => {
          const messageObj = {
            institutionName,
            message: '',
            title: investmentLongName
          };
          messageObj.institutionName = data.institutionName;
          messageObj.title = data.investmentLongName;
          const fundActiveDate = data.activeDate ? moment(data.activeDate).format('MMMM D') : undefined;
          const fundInactiveDate = data.inactiveDate ? moment(data.inactiveDate).format('MMMM D') : undefined;
          messageObj.message = computeModalMessage({ activeDate: fundActiveDate, inactiveDate: fundInactiveDate, mode: data.fundMode });
          dynamicGreetingData.modalMessages.push(messageObj);
        });
        dynamicGreetingData.linkText = 'Read more.';
        dynamicGreetingData.greetingText = 'The status of this investment is changing in your retirement plans. ';
        break;
      case DYNAMIC_GREETING_MODES.notAvailable:
        dynamicGreetingData.greetingText = 'This investment is not available in any of your retirement plans. ';
        dynamicGreetingData.linkText = 'Find similar investments.';
        dynamicGreetingData.url = `https://${urlPrefix}${DYNAMIC_GREETING_URLS.chooseTransaction}`;
        dynamicGreetingData.variant = 'DynamicGreetingX';
        break;
      case DYNAMIC_GREETING_MODES.opening:
        dynamicGreetingData.greetingText = `This investment is opening in your ${institutionName} retirement plan on ${formattedActiveDate}. `;
        messageData.message = computeModalMessage({ activeDate: formattedActiveDate, mode: investmentChangingMode });
        dynamicGreetingData.linkText = 'Read more.';
        dynamicGreetingData.modalMessages.push(messageData);
        break;
      case DYNAMIC_GREETING_MODES.restricted:
        dynamicGreetingData.greetingText = `This investment is restricted or closed in your ${institutionName} retirement plan. `;
        dynamicGreetingData.variant = 'DynamicGreetingX';
        messageData.message = computeModalMessage({ mode: investmentChangingMode });
        dynamicGreetingData.linkText = 'Read more.';
        dynamicGreetingData.modalMessages.push(messageData);
        break;
      default:
        break;
    }
    dynamicGreetingArr.push(dynamicGreetingData);
  });
  return dynamicGreetingArr;
};

export const getIsDynamicGreetingShown = ({ dynamicGreetingMode }) => {
  if (!dynamicGreetingMode || Object.keys(dynamicGreetingMode).length === 0 ) {
    return false;
  }
  return dynamicGreetingMode && (dynamicGreetingMode[0] !== DYNAMIC_GREETING_MODES.hide);
};
